import styled, { useTheme } from 'styled-components';
import {
  BodyLarge,
  Heading1Text,
  TitleLarge,
  TitleMedium,
} from '../../../../components/Texts';
import Button from '../../../../components/Button';
import WidthContainer from '../../../../components/WidthContainer';
import { useNavigate } from 'react-router-dom';
import {
  PHONE_MEDIA_QUERY,
  useMediaQuery,
} from '../../../../common/responsive';
import { useEffect, useRef, useState } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import TextInput from '../../../../components/Inputs/Text';
import CountrySelector from '../../../../components/Inputs/CountrySelect/index';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import { selectPrivyToken } from '../../../../redux/slices/auth';
import { useGetCurrentUserProfileQuery } from '../../../../redux/api/authorized/auth';
import { useCreatePhysicalGiftCardOrderMutation } from '../../../../redux/api/public/physical';
import { toast } from 'react-toastify';

const InnerContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  margin: 10px 0px;
`;

const BackgroundColor = styled.div`
  background-color: ${({ theme }) => theme.primary.background};
  padding: 40px 0px;
  position: absolute;
  width: 100%;
  left: 0px;
`;

const Container = styled.div`
  min-height: 600px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    text-align: center;
    justify-content: center;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: row;

  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: row;
    gap: 10px;
  }
`;

const FormContainer = styled.form`
  background-color: ${({ theme }) => theme.background.primary};
  width: 100%;
  padding: 40px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;

  @media ${PHONE_MEDIA_QUERY} {
    padding: 20px;
  }
`;

const schema = yup
  .object({
    email: yup
      .string()
      .email('Email must be a valid email')
      .required('Email is required'),
    telegram: yup.string().required('Telegram is required'),
    amount: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null ? undefined : value,
      )
      .typeError('Number of cards must be a number')
      .required('Number of cards is required')
      .min(10, 'Amount of cards must be at least 10'),
    tokensPerCard: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue == null ? undefined : value,
      )
      .typeError('Number of tokens per card must be a number')
      .required('Number of tokens per card is required'),
    projectURL: yup
      .string("Your project's token address")
      .required("Your project's token address is required"),
    country: yup.string().required('Country is required'),
    state: yup.string().required('State is required'),
    street: yup.string().required('Street is required'),
    zipCode: yup.string().required('Zip code is required'),
  })
  .required();

const Title = styled.h2`
  font-size: 48px;
  font-weight: 500;
  line-height: 48px;
  letter-spacing: -2%;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 2rem;
    line-height: 2.2rem;
  }
`;

const Form = () => {
  const backgroundRef = useRef(null);
  const [siblingHeight, setSiblingHeight] = useState(undefined);

  const privyToken = useSelector(selectPrivyToken);
  const { data: currentUser } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });
  const [sent, setSent] = useState(false);
  const [createPhysicalGiftCardOrder, { isLoading }] =
    useCreatePhysicalGiftCardOrderMutation();

  const defaultEmail = currentUser?.email || '';

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      country: 'United States',
      email: defaultEmail,
    },
  });

  useEffect(() => {
    const updateHeight = () => {
      if (backgroundRef.current) {
        setSiblingHeight(backgroundRef.current.offsetHeight);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);

    if (backgroundRef.current) {
      observer.observe(backgroundRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const navigate = useNavigate();

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const theme = useTheme();

  const onSubmit = async (data) => {
    const response = await createPhysicalGiftCardOrder(data);
    if (response.error) {
      toast.error('There was an error creating the order.');
    } else {
      toast.success('Order created.');
      setSent(true);
    }
  };

  return (
    <>
      <BackgroundColor ref={backgroundRef} id='form'>
        <WidthContainer>
          <Container>
            <div
              style={{
                display: 'flex',
                gap: 24,
                flexDirection: 'column',
                width: '100%',
                marginBottom: isMobile ? '30px' : '0px',
              }}
            >
              <HeaderContainer>
                <Title
                  style={{
                    color: theme.text.title,
                    width: '100%',
                  }}
                >
                  <span style={{ color: theme.primary.primary }}>Free</span>{' '}
                  Gift Cards
                  <br />
                  For Token Projects
                </Title>
              </HeaderContainer>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <TitleMedium>
                  Get your first 50 printed cards on <br />
                  us. Then just $1 per card.
                </TitleMedium>
                <p style={{ fontSize: '12px', color: theme.text.secondary }}>
                  * Free shipping within the US. International orders subject to
                  standard shipping rates.
                </p>
              </div>
            </div>
            <InnerContainer>
              <FormContainer onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  name='email'
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      inputMode='email'
                      label='Email'
                      type='text'
                      error={errors?.email?.message}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name='telegram'
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      inputMode='telegram'
                      label='Telegram'
                      type='text'
                      error={errors?.telegram?.message}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name='projectURL'
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      label="Your project's token address"
                      type='text'
                      error={errors?.projectURL?.message}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name='amount'
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      inputMode='numeric'
                      label='Number of cards'
                      type='text'
                      error={errors?.amount?.message}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name='tokensPerCard'
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      tooltip='The number of tokens you are going to add to each card.'
                      inputMode='numeric'
                      label='Number of tokens per card'
                      type='text'
                      error={errors?.tokensPerCard?.message}
                      {...field}
                    />
                  )}
                />
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    width: '100%',
                    alignItems: 'center',
                  }}
                >
                  <Controller
                    name='country'
                    control={control}
                    render={({ field }) => (
                      <CountrySelector
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <Controller
                    name='state'
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label='State'
                        type='text'
                        error={errors?.state?.message}
                        {...field}
                      />
                    )}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    width: '100%',
                    alignItems: 'center',
                  }}
                >
                  <Controller
                    name='street'
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label='Street'
                        type='text'
                        error={errors?.street?.message}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name='zipCode'
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label='Zip Code'
                        type='text'
                        error={errors?.zipCode?.message}
                        {...field}
                      />
                    )}
                  />
                </div>
                <Button
                  fullWidth={isMobile}
                  type='submit'
                  disabled={!isValid || isSubmitting || sent}
                  loading={isLoading}
                >
                  {sent ? 'ORDER CREATED' : 'ORDER NOW'}
                </Button>
                {sent && (
                  <div
                    style={{
                      color: theme.text.secondary,
                      fontSize: '14px',
                      textAlign: isMobile ? 'center' : 'left',
                    }}
                  >
                    We will contact you via email or telegram for further
                    instructions.
                  </div>
                )}
              </FormContainer>
            </InnerContainer>
          </Container>
        </WidthContainer>
      </BackgroundColor>
      <div style={{ height: siblingHeight }} />
    </>
  );
};

export default Form;
